import { CSSProperties, ElementType, ReactNode, createElement, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { cn } from '@/shared/utils'

type ButtonTone = 'denim' | 'ink'

interface LandingButtonProps {
  to: string
  children: ReactNode
  tone?: ButtonTone
  className?: string
}

/** The landing page's one button. Hash targets stay plain anchors so the browser scrolls to them. */
export function LandingButton({ to, children, tone = 'denim', className }: LandingButtonProps) {
  const classes = cn('l-btn', tone === 'ink' && 'l-btn--ink', className)
  if (to.startsWith('#')) {
    return (
      <a href={to} className={classes}>
        {children}
      </a>
    )
  }
  return (
    <Link to={to} className={classes}>
      {children}
    </Link>
  )
}

interface RevealProps {
  as?: ElementType
  /** Stagger within a section, in ms */
  delay?: number
  className?: string
  children: ReactNode
}

/** Rises and fades once as it enters the viewport. Reduced motion leaves it in place (see landing.css). */
export function Reveal({ as = 'div', delay = 0, className, children }: RevealProps) {
  const ref = useRef<HTMLElement>(null)
  const [shown, setShown] = useState(false)

  useEffect(() => {
    const element = ref.current
    if (!element) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShown(true)
          observer.disconnect()
        }
      },
      // The huge top margin counts anything already scrolled past, so a section skipped
      // by an anchor jump or a fast scroll is never left blank when you scroll back.
      { rootMargin: '100000px 0px -8% 0px' },
    )
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  return createElement(
    as,
    {
      ref,
      className,
      'data-reveal': '',
      'data-shown': shown,
      style: { '--l-reveal-delay': `${delay}ms` } as CSSProperties,
    },
    children,
  )
}

interface RedactedProps {
  hidden: boolean
  children: ReactNode
  /** What a screen reader hears while the value is covered */
  hiddenLabel: string
  /** Position in a group, used to stagger the bars */
  index?: number
}

/** A value a share link is hiding, covered by a bar. */
export function Redacted({ hidden, children, hiddenLabel, index = 0 }: RedactedProps) {
  return (
    <span className="l-redact" data-hidden={hidden} style={{ '--l-i': index } as CSSProperties}>
      <span className="l-redact__value" aria-hidden={hidden}>
        {children}
      </span>
      {hidden && <span className="sr-only">{hiddenLabel}</span>}
      <span className="l-redact__bar" aria-hidden />
    </span>
  )
}
